import { difference } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { INPUT_TYPES } from '../../../Inputs/ValidatedInputTypes';
import ModalEditor from '../../../Modals/ModalEditor';
import { defaultUnselectedFields } from './hooks';
import SortableColumnSelector from './SortableColumnSelector';
import { ColumnDescriptor } from './types';

interface HookResult {
  object: FormObject;
  setObject: React.Dispatch<React.SetStateAction<FormObject>>;
  handleCallBack: (obj: FormObject) => void;
  onVisibleFieldsSideEffect: (
    current: FormObject,
    next: string[]
  ) => Partial<FormObject>;
  handleValidate: (obj: any) => { visible_fields: string } | undefined;
  inputs: object[];
}
interface HookProps {
  onCallback: (selectedColumns: string[], unselectedColumns: string[]) => void;
  columnDescriptors: ColumnDescriptor[];
  selectedColumns: string[];
  defaultUnselectedColumns?: string[];
  additionalText?: React.ReactNode;
}

type ColumnSelectorHook = (props: HookProps) => HookResult;

interface ModalProps extends HookProps {
  modalIsOpen: boolean;
  onToggle: () => void;
}
interface FormObject {
  visible_fields: string[];
  unselected_columns_stash: string[];
}

export const useColumnSelector: ColumnSelectorHook = ({
  onCallback,
  columnDescriptors,
  defaultUnselectedColumns = [],
  selectedColumns,
  additionalText,
}) => {
  const { formatMessage } = useIntl();
  const [object, setObject] = useState<FormObject>(() => {
    const defaultComputedUnselectedColumns = defaultUnselectedFields(
      columnDescriptors,
      defaultUnselectedColumns
    );
    return {
      visible_fields: selectedColumns,
      unselected_columns_stash: defaultComputedUnselectedColumns,
    };
  });

  useEffect(() => {
    setObject((object) => ({
      ...object,
      visible_fields: difference(
        selectedColumns,
        object.unselected_columns_stash
      ),
    }));
  }, [selectedColumns]);

  const handleCallBack = useCallback(
    (obj: FormObject): void => {
      onCallback(obj.visible_fields, obj.unselected_columns_stash);
    },
    [onCallback]
  );

  const onVisibleFieldsSideEffect = useCallback(
    (current: FormObject, next: string[]): Partial<FormObject> => {
      const removedItems = difference(current.visible_fields, next);
      const addedItems = difference(next, current.visible_fields);
      return {
        unselected_columns_stash: difference(
          [...current.unselected_columns_stash, ...removedItems],
          addedItems
        ),
      };
    },
    []
  );

  const handleValidate = useCallback(
    (obj) => {
      if (obj.visible_fields.length === 0) {
        return {
          visible_fields: formatMessage({
            id: 'app.views.widgets.people.filterable_people_table_column_selector_modal.validation_error.select_at_least_one_field',
            defaultMessage: 'Select at least one field',
          }),
        };
      }
    },
    [formatMessage]
  );

  const inputs = useMemo(() => {
    return [
      ...(additionalText
        ? [
            {
              type: INPUT_TYPES.CUSTOM_INPUT,
              name: '_additional_text',
              component: additionalText,
            },
          ]
        : []),
      {
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        name: 'visible_fields',
        component: SortableColumnSelector,
        columnDescriptors,
        onChangeSideEffects: onVisibleFieldsSideEffect,
      },
    ];
  }, [onVisibleFieldsSideEffect, columnDescriptors, additionalText]);

  return {
    object,
    setObject,
    handleCallBack,
    onVisibleFieldsSideEffect,
    handleValidate,
    inputs,
  };
};

const FilterablePeopleTableColumnSelectorModal: FC<ModalProps> = ({
  modalIsOpen,
  onToggle,
  onCallback,
  columnDescriptors,
  selectedColumns,
  additionalText,
  defaultUnselectedColumns = [],
}) => {
  const { formatMessage } = useIntl();

  const { object, setObject, handleCallBack, handleValidate, inputs } =
    useColumnSelector({
      onCallback,
      columnDescriptors,
      selectedColumns,
      defaultUnselectedColumns,
      additionalText,
    });

  return (
    <ModalEditor
      isOpen={modalIsOpen}
      toggle={onToggle}
      title={formatMessage({
        id: 'app.views.widgets.people.filterable_people_table_column_selector_modal.title.select_fields_to_display',
        defaultMessage: 'Select fields to display',
      })}
      object={object}
      onValidate={handleValidate}
      onChange={setObject}
      callback={handleCallBack}
      submitText={formatMessage({
        id: 'app.views.widgets.people.filterable_people_table_column_selector_modal.submit_text.apply',
        defaultMessage: 'Apply',
      })}
      inputs={inputs}
      disableUnsavedChangesPrompt={true}
    />
  );
};

export default FilterablePeopleTableColumnSelectorModal;
